/**
 * ==========================================================================
 *  The two things in the yard that are on fire, or nearly: the plumes off the
 *  chimney and the water it heats, and the flames inside the kamiina's box.
 *
 *  The plumes are sprites rather than geometry, because smoke has no shape
 *  from any angle you can walk to - and because a billboard that always faces
 *  you is the one cheap trick that survives being run past at 30 km/h. The
 *  sprites are made once and the same handful is walked round a loop for the
 *  whole session; `src/render/Plume.ts` owns where each one is at a given
 *  moment, and this end is three.js and nothing else.
 *
 *  Both are driven by wall-clock render time. The fire in the kamiina is lit
 *  by the simulation - `Kamiina` decides whether there are klapit in it - but
 *  how the flames move once it is lit is nobody's business but this file's.
 *
 *  Rendering only. Nothing in this file may ever be imported by `src/sim`.
 * ==========================================================================
 */

import * as THREE from 'three';
import { FIRE } from '../config/gameplay';
import { plumeSeeds, puffPose, samplePuff, type PuffSeed } from './Plume';
import { buildSmokePuff } from './decals';
import { mulberry32 } from '../utils/prng';
import type { LevelDefinition, SmokeSpec } from '../world/Level';

/**
 * Wood smoke against a cold sky: warm grey, and never white. White smoke on a
 * blue-grey morning reads as a rendering error rather than as a fire.
 *
 * The default rather than the rule: the savukone in front of the XLMOTO tent
 * carries its own colour, because glycol out of a nozzle really is white. See
 * `SmokeSpec.color`.
 */
const SMOKE_COLOUR = 0xd2cec4;

/** One sprite in a plume, plus its fixed place in that plume's loop. */
interface Puff {
  sprite: THREE.Sprite;
  spec: SmokeSpec;
  seed: PuffSeed;
}

export class Plumes {
  /** Every puff in the yard, from every chimney. See `renderSmoke`. */
  private readonly puffs: Puff[] = [];
  /** One scratch pose, reused: see the note on `samplePuff`. */
  private readonly puffScratch = puffPose();
  /** The flames in the kamiina's firebox. */
  private fireGroup: THREE.Group | null = null;

  constructor(private readonly level: LevelDefinition) {}

  /** The plumes and the firebox, for the caller to put in the scene. */
  build(): THREE.Object3D[] {
    const out: THREE.Object3D[] = [];
    this.buildSmoke(out);
    this.buildFire(out);
    return out;
  }

    /**
     * The plumes: the kamiina's chimney, and the steam off the water it heats.
     *
     * Sprites rather than geometry, because smoke has no shape from any angle
     * you can walk to - and because a billboard that always faces you is the one
     * cheap trick that survives being run past at 30 km/h.
     *
     * The sprites are made here and never made again - `src/render/Plume.ts`
     * owns where they go, and walks the same handful round one loop for the
     * whole session. This end of it is three.js and nothing else.
     */
    private buildSmoke(out: THREE.Object3D[]): void {
      if (this.level.smoke.length === 0) return;

      // One canvas for every puff in the yard: what makes them different is
      // where they are in the climb, not what they are drawn from.
      const map = buildSmokePuff();
      // Authoring randomness, once, at load - the same allowance `prng.ts` is
      // written for. Nothing here is ever stepped by the simulation.
      const rng = mulberry32(0x5a0f7e);

      for (const spec of this.level.smoke) {
        plumeSeeds(spec.count, rng).forEach((seed, i) => {
          const sprite = new THREE.Sprite(
            new THREE.SpriteMaterial({
              map,
              color: spec.color ?? SMOKE_COLOUR,
              transparent: true,
              opacity: 0,
              // Smoke that writes depth cuts a hole in the smoke behind it.
              depthWrite: false,
            }),
          );
          sprite.name = `${spec.name}-${i}`;
          this.puffs.push({ sprite, spec, seed });
          out.push(sprite);
        });
      }
    }

    /**
     * Moves every puff along its climb. Render-rate, visuals only.
     *
     * Driven by wall-clock render time rather than by `simulationTime`, which is
     * the one thing worth being deliberate about here: the chimney is scenery,
     * and scenery does not stop drawing because the run has not started yet.
     */
    renderSmoke(renderTime: number): void {
      for (const puff of this.puffs) {
        const pose = samplePuff(puff.spec, puff.seed, renderTime, this.puffScratch);
        puff.sprite.position.set(pose.x, pose.y, pose.z);
        puff.sprite.scale.set(pose.radius * 2, pose.radius * 2, 1);

        const material = puff.sprite.material as THREE.SpriteMaterial;
        material.opacity = pose.opacity;
        material.rotation = pose.rotation;
      }
    }

    /**
     * The fire in the kamiina's hatch.
     *
     * Flat quads rather than a particle system, and unlit rather than emissive:
     * a firebox at nine in the morning is a slot of orange in a dark plate, and
     * the whole of what sells it is that the slot *moves*. `FIRE` in
     * src/config/gameplay.ts owns the timing.
     *
     * Render-only, like the smoke. Nothing here is stepped by the simulation and
     * the fingerprint never sees it, so the fire can be re-tuned on looks alone.
     */
    private buildFire(out: THREE.Object3D[]): void {
      const hatch = this.level.kamiina.hatch;
      const group = new THREE.Group();
      group.position.set(hatch.x, hatch.y, hatch.z);

      // A four-sided cone per tongue rather than a quad. A flame is a taper, and
      // a taper drawn as a rectangle reads as a strip of orange paint - which is
      // exactly what the ember sliver under the door already is, and having two
      // of those would say the fire is a texture.
      const tongue = new THREE.ConeGeometry(0.5, 1, 4, 1, true);
      for (let i = 0; i < FIRE.tongues; i++) {
        const flame = new THREE.Mesh(
          tongue,
          new THREE.MeshBasicMaterial({
            color: i % 2 === 0 ? FIRE.hot : FIRE.cool,
            transparent: true,
            opacity: 0,
            depthWrite: false,
            side: THREE.DoubleSide,
          }),
        );
        flame.name = `liekki-${i}`;
        // Behind the plate, spread across the width of the hatch.
        flame.position.set(-FIRE.inset, 0, ((i + 0.5) / FIRE.tongues - 0.5) * FIRE.width);
        group.add(flame);
      }

      this.fireGroup = group;
      out.push(group);
    }

    /**
     * The fire, at whatever it is doing this frame.
     *
     * `fedFor` is seconds since the klapi went in, or null while it has not. The
     * difference between those two is the whole point of the thing: embers you
     * have to be looking for, and then a fire you can see from the ansalanka.
     */
    renderFire(renderTime: number, fedFor: number | null): void {
      const group = this.fireGroup;
      if (!group) return;

      // Nearly out, then a flare as the log catches, then a settled fire.
      const flare =
        fedFor === null
          ? 0
          : fedFor < FIRE.flareDuration
            ? Math.sin((fedFor / FIRE.flareDuration) * Math.PI) * FIRE.flarePeak
            : 0;
      const base = fedFor === null ? FIRE.embers : FIRE.burning;
      const strength = base + flare;

      group.children.forEach((child, i) => {
        const flame = child as THREE.Mesh;
        // Each tongue on its own beat, so the row never pulses as one block.
        const beat = renderTime * FIRE.flicker + i * 1.7;
        const wobble = 0.72 + 0.28 * Math.sin(beat) * Math.sin(beat * 0.37 + 1.1);
        const tall = FIRE.height * strength * wobble;
        const wide = (FIRE.width / FIRE.tongues) * (1.4 + 0.5 * wobble);
        flame.scale.set(wide, tall, wide);
        // Standing on the bed of the firebox rather than floating in the middle
        // of the doorway: the base stays put and the tip is what moves.
        flame.position.y = tall / 2 - FIRE.height * 0.42;
        const material = flame.material as THREE.MeshBasicMaterial;
        material.opacity = Math.min(1, strength * wobble * FIRE.opacity);
      });
    }
}
